import React, { useEffect, useState } from 'react';
import {
  Home,
  Sparkles,
  MessageCircle,
  Users,
  Wand2,
  FileText,
  CheckCircle,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  X,
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import './AppSidebar.css';
import logo from '../assets/logo.svg';

export default function AppSidebar({
  view,
  onNavigate,
  userRole = 'client',
  user,
  collapsed = false,
  onToggleCollapse,
}) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const isMobileSidebarVisible = mobileMenuOpen;

  useEffect(() => {
    if (!user?.id) return;

    const checkUnread = async () => {
      const col = userRole === 'lawyer' ? 'lawyer_id' : 'client_id';
      const { data: convs } = await supabase
        .from('conversations')
        .select('id')
        .eq(col, user.id);

      if (!convs?.length) {
        setUnreadCount(0);
        return;
      }

      const convIds = convs.map((c) => c.id);
      const { count } = await supabase
        .from('messages')
        .select('id', { count: 'exact', head: true })
        .in('conversation_id', convIds)
        .eq('is_read', false)
        .neq('sender_id', user.id);

      setUnreadCount(count || 0);
    };

    checkUnread();
    const interval = setInterval(checkUnread, 15000);
    return () => clearInterval(interval);
  }, [user?.id, userRole]);



  const navItems = [
    { id: 'dashboard', label: 'Home', icon: <Home size={18} strokeWidth={2} /> },
    { id: 'landing', label: 'Chatbot', icon: <Sparkles size={18} strokeWidth={2} /> },
    {
      id: 'messages',
      label: 'Inbox',
      icon: (
        <span className="app-sidebar-icon-wrap">
          <MessageCircle size={18} strokeWidth={2} />
          {unreadCount > 0 && (
            <span className="app-sidebar-unread-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
          )}
        </span>
      ),
    },
    ...(userRole === 'client'
      ? [{ id: 'profiles', label: 'Advocates', icon: <Users size={18} strokeWidth={2} /> }]
      : []),
    ...(userRole === 'client'
      ? [{ id: 'recommend', label: 'Recommend', icon: <Wand2 size={18} strokeWidth={2} /> }]
      : []),
    { id: 'summarize', label: 'Case AI', icon: <FileText size={18} strokeWidth={2} /> },
    { id: 'compliance', label: 'Compliance', icon: <CheckCircle size={18} strokeWidth={2} /> },
  ];

  const handleNav = (id) => {
    onNavigate(id);
    setMobileMenuOpen(false);
  };

  const handleMobileToggle = () => {
    setMobileMenuOpen((v) => !v);
  };

  return (
    <>
      <div className="mobile-header-bar">
        <div className="mobile-header-brand">
          <img src={logo} alt="LawEase" className="mobile-header-logo" />
          <span>LawEase</span>
        </div>
      </div>
      <button
        className={`app-sidebar-mobile-toggle ${isMobileSidebarVisible ? 'open' : ''}`}
        onClick={handleMobileToggle}
        aria-label="Toggle navigation menu"
        aria-expanded={isMobileSidebarVisible}
        type="button"
      >
        {isMobileSidebarVisible ? <X size={20} /> : <Menu size={20} />}
      </button>

      <aside className={`app-sidebar ${mobileMenuOpen ? 'mobile-open' : ''} ${collapsed ? 'collapsed' : ''}`} data-tour="Sidebar / Navigation Menu">
        <div className="app-sidebar-topbar">
          <div className="app-sidebar-brand" title="LawEase">
            <img src={logo} alt="LawEase" className="app-sidebar-brand-logo" />
            <span className="app-sidebar-brand-text">LawEase</span>
          </div>
          <button
            className="app-sidebar-collapse-btn"
            onClick={() => onToggleCollapse && onToggleCollapse()}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            type="button"
          >
            {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
          </button>
        </div>

        <nav className="app-sidebar-nav">
          {navItems.map((item) => (
            <button
              key={item.id}
              className={`app-sidebar-item ${view === item.id ? 'active' : ''}`}
              onClick={() => handleNav(item.id)}
              title={collapsed ? item.label : ''}
              data-tour={`Sidebar / ${item.label}`}
            >
              <span className="app-sidebar-icon">{item.icon}</span>
              <span className="app-sidebar-label">{item.label}</span>
            </button>
          ))}
        </nav>
      </aside>
    </>
  );
}
